import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { 
  login as apiLogin, 
  logout as apiLogout, 
  checkAuth,
  clearUser,
  isSupportOnly as computeIsSupportOnly,
  type AuthUser, 
  type LoginCredentials 
} from '../api';

/**
 * Auth Context
 * 
 * Provides authentication state and methods throughout the app.
 */

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** Scoped customer-support user: User Accounts section only, read-mostly. */
  isSupportOnly: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check auth status on mount
  useEffect(() => {
    async function initAuth() {
      try {
        const currentUser = await checkAuth();
        setUser(currentUser);
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    }

    initAuth();
  }, []);

  // Listen for unauthorized events from API client
  useEffect(() => {
    function handleUnauthorized() {
      setUser(null);
      clearUser();
      setError('Your session has expired. Please log in again.');
    }

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const loggedInUser = await apiLogin(credentials);
      setUser(loggedInUser);
    } catch (err) {
      let message = 'Login failed. Please try again.';
      if (err instanceof Error) {
        message = err.message;
      }
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await apiLogout();
    } finally {
      setUser(null);
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isSupportOnly: computeIsSupportOnly(user),
    isLoading,
    login,
    logout,
    error,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default useAuth;
